import { useDateFilter } from '@/common/hooks/useDateFilter'
import { useDateRangeStack } from '@/common/hooks/useDateRangeStack'
import { Button } from '@/common/ui/button'
import { Calendar } from '@/common/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import { CalendarIcon } from '@radix-ui/react-icons'
import { format } from 'date-fns'
import * as React from 'react'
import { DateRange } from 'react-day-picker'
import { parseAsString, useQueryState } from 'nuqs'

export function DateRangePicker() {
	const { dateFilter, setDateFilter } = useDateFilter()
	const { resetStacks } = useDateRangeStack()
	const [open, setOpen] = React.useState(false)
	const [, setDateRange] = useQueryState(
		'dateRange',
		parseAsString.withDefault(''),
	)

	const [dateRange, setDateRangeState] = React.useState<DateRange | undefined>({
		from: dateFilter.startDate
			? new Date(`${dateFilter.startDate}T00:00:00`)
			: undefined,
		to: dateFilter.endDate
			? new Date(`${dateFilter.endDate}T00:00:00`)
			: undefined,
	})

	// Update dateRange when dateFilter changes
	React.useEffect(() => {
		setDateRangeState({
			from: dateFilter.startDate
				? new Date(`${dateFilter.startDate}T00:00:00`)
				: undefined,
			to: dateFilter.endDate
				? new Date(`${dateFilter.endDate}T00:00:00`)
				: undefined,
		})
	}, [dateFilter.startDate, dateFilter.endDate])

	const handleConfirm = () => {
		if (dateRange?.from && dateRange?.to) {
			setDateRangeState({
				from: dateRange.from,
				to: dateRange.to,
			})
			setDateFilter({
				startDate: format(dateRange.from, 'yyyy-MM-dd'),
				endDate: format(dateRange.to, 'yyyy-MM-dd'),
			})
			// Reset the dateRange query parameter since we're using a custom date range
			setDateRange('')
			setOpen(false)
		}
	}

	const resetDate = () => {
		setDateRangeState(undefined)
		setDateFilter({
			startDate: null,
			endDate: null,
		})
		// Reset the dateRange query parameter to 'all'
		setDateRange('all')
		// Reset the stack states
		resetStacks()
	}

	// Format date for mobile display (shorter format)
	const formatDateForDisplay = (from: Date, to: Date) => {
		const fromFormatted = format(from, 'dd/MM')
		const toFormatted = format(to, 'dd/MM')

		// If same year, show short format
		if (from.getFullYear() === to.getFullYear()) {
			return `${fromFormatted} - ${toFormatted}`
		}

		// Different years, show full format
		return `${format(from, 'dd/MM/yy')} - ${format(to, 'dd/MM/yy')}`
	}

	return (
		<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						size="sm"
						className="w-full justify-start text-left font-normal sm:w-auto"
					>
						<CalendarIcon className="mr-1 h-4 w-4 flex-shrink-0 sm:mr-2" />
						<span className="truncate">
							{dateRange?.from && dateRange?.to ? (
								<>
									{/* Mobile: Short format */}
									<span className="sm:hidden">
										{formatDateForDisplay(dateRange.from, dateRange.to)}
									</span>
									{/* Desktop: Full format */}
									<span className="hidden sm:inline">
										{`${format(dateRange.from, 'yyyy-MM-dd')} - ${format(
											dateRange.to,
											'yyyy-MM-dd',
										)}`}
									</span>
								</>
							) : (
								<>
									<span className="sm:hidden">เลือกวันที่</span>
									<span className="hidden sm:inline">เลือกช่วงวันที่</span>
								</>
							)}
						</span>
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-auto p-3 sm:p-4" align="start">
					<Calendar
						mode="range"
						selected={dateRange}
						onSelect={(range: DateRange | undefined) =>
							setDateRangeState(range)
						}
						className="rounded-md border-0"
					/>
					<div className="mt-3 flex justify-end sm:mt-4">
						<Button
							onClick={handleConfirm}
							disabled={!dateRange?.from || !dateRange?.to}
							size="sm"
						>
							ยืนยัน
						</Button>
					</div>
				</PopoverContent>
			</Popover>
			<Button
				onClick={resetDate}
				variant="outline"
				size="sm"
				className="w-full sm:w-auto"
			>
				<span className="sm:hidden">ล้าง</span>
				<span className="hidden sm:inline">ล้างวันที่</span>
			</Button>
		</div>
	)
}
